import { Badge } from '@ovComponents/2-component/badge/badge';
import { Chip } from '@ovComponents/2-component/chip/chip';
import {
  Table,
  TableBody,
  TableHead,
  TableHeadCell,
  TableRow,
} from '@ovComponents/2-component/table/table';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Skeleton,
  Typography,
  useTheme,
} from 'hephaestus/web';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../../../../hooks/useLocalization';
import type { RunReportPortfolioFilters } from '../../types/runTable.types';
import type {
  ReviewTradeCounts,
  ReviewTradePortfolioGroup,
} from '../../types/tradeReview.types';
import {
  buildReviewTradeFilterIndex,
  filterReviewTradeGroups,
  hasActiveReviewTradeFilters,
  hasReviewTradeNotes,
  type ReviewTradeStatusFilter,
} from '../../utils/tradeReview/tradeReviewPresentation';
import { bulkRebalanceTableHeadDividerSx } from '../auditGrid/tableStyles';
import { BulkRebalancePortfolioFilterBar } from '../shared/portfolioFilterBar';
import { ReviewTradeCancelDialogs } from './reviewTradeCancelDialogs';
import { ReviewTradeGroupRows } from './reviewTradeGroupRows';

export type BulkRebalanceReviewTradesPanelProps = {
  loading: boolean;
  tradesFullyLoaded: boolean;
  evaluating: boolean;
  cancelling: boolean;
  verifying: boolean;
  groups: ReviewTradePortfolioGroup[];
  counts: ReviewTradeCounts;
  totalCount: number;
  requireApproval: boolean;
  canCancel: boolean;
  canApprove: boolean;
  showProceedButton: boolean;
  proceedDisabledReason?: string;
  proceeding: boolean;
  onCancelTrades: (ids: string[]) => Promise<void>;
  onApproveTrades: (ids: string[]) => Promise<void>;
};

const STATUS_FILTER_OPTIONS: ReviewTradeStatusFilter[] = [
  'all',
  'ready',
  'held',
  'pending',
  'needsApproval',
  'canceled',
];

export const BulkRebalanceReviewTradesPanel = ({
  loading,
  tradesFullyLoaded,
  evaluating,
  cancelling,
  verifying,
  groups,
  counts,
  totalCount,
  requireApproval,
  canCancel,
  canApprove,
  showProceedButton,
  proceedDisabledReason,
  proceeding,
  onCancelTrades,
  onApproveTrades,
}: BulkRebalanceReviewTradesPanelProps) => {
  const { translateBackendString } = useLocalization();
  const { t } = useTranslation(['bulkRebalance']);
  const theme = useTheme();
  const tableHeadDividerSx = bulkRebalanceTableHeadDividerSx(theme);
  const { localizedDate } = useLocalization();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [expandedGroupIds, setExpandedGroupIds] = useState<Set<string>>(
    new Set(),
  );
  const [portfolioFilters, setPortfolioFilters] =
    useState<RunReportPortfolioFilters>({});
  const [statusFilter, setStatusFilter] =
    useState<ReviewTradeStatusFilter>('all');
  const [confirmCancelAll, setConfirmCancelAll] = useState(false);
  const [confirmCancelSelected, setConfirmCancelSelected] = useState(false);
  const [confirmCancelRowId, setConfirmCancelRowId] = useState<string | null>(
    null,
  );
  const filterAutoExpandRef = useRef<{
    filterKey: string;
    expandedNonEmpty: boolean;
  } | null>(null);

  const searchQuery = portfolioFilters.searchQuery ?? '';
  const reviewFilterInput = {
    searchQuery,
    statusFilter,
    modelPortfolioId: portfolioFilters.modelPortfolioId,
    financialProductId: portfolioFilters.financialProductId,
  };
  const filteredGroups = useMemo(
    () =>
      filterReviewTradeGroups(
        translateBackendString,
        groups,
        reviewFilterInput,
      ),
    [
      groups,
      searchQuery,
      statusFilter,
      portfolioFilters.modelPortfolioId,
      portfolioFilters.financialProductId,
    ],
  );
  const filterIndex = useMemo(
    () =>
      buildReviewTradeFilterIndex(groups, {
        modelPortfolioId: portfolioFilters.modelPortfolioId,
        financialProductId: portfolioFilters.financialProductId,
      }),
    [
      groups,
      portfolioFilters.financialProductId,
      portfolioFilters.modelPortfolioId,
    ],
  );
  const hasActiveFilters = hasActiveReviewTradeFilters(reviewFilterInput);
  const filteredTradeCount = useMemo(
    () => filteredGroups.reduce((total, group) => total + group.rows.length, 0),
    [filteredGroups],
  );
  const visibleGroupIds = useMemo(
    () => filteredGroups.map((group) => group.portfolioReportId),
    [filteredGroups],
  );
  const allVisibleExpanded =
    visibleGroupIds.length > 0 &&
    visibleGroupIds.every((id) => expandedGroupIds.has(id));
  const showTradeColumns = visibleGroupIds.some((id) =>
    expandedGroupIds.has(id),
  );
  const visibleStatusFilters = useMemo(
    () =>
      STATUS_FILTER_OPTIONS.filter((filter) => {
        if (filter === 'all') {
          return true;
        }

        if (filter === 'needsApproval') {
          return requireApproval && counts.unverifiedActionable > 0;
        }

        if (filter === 'canceled') {
          return counts.canceled > 0;
        }

        if (filter === 'ready') {
          return counts.ready > 0;
        }

        if (filter === 'held') {
          return counts.held > 0;
        }

        if (filter === 'pending') {
          return counts.pending > 0;
        }

        return false;
      }),
    [
      counts.canceled,
      counts.held,
      counts.pending,
      counts.ready,
      counts.unverifiedActionable,
      requireApproval,
    ],
  );

  useEffect(() => {
    if (!hasActiveFilters) {
      filterAutoExpandRef.current = null;
      return;
    }

    const filterKey = `${searchQuery}\0${statusFilter}\0${portfolioFilters.modelPortfolioId ?? ''}\0${portfolioFilters.financialProductId ?? ''}`;
    const matchingGroupIds = filterReviewTradeGroups(
      translateBackendString,
      groups,
      reviewFilterInput,
    ).map((group) => group.portfolioReportId);
    const prev = filterAutoExpandRef.current;
    const filterChanged = prev?.filterKey !== filterKey;
    const shouldExpandLateLoad =
      !filterChanged && !prev?.expandedNonEmpty && matchingGroupIds.length > 0;

    if (!filterChanged && !shouldExpandLateLoad) {
      return;
    }

    filterAutoExpandRef.current = {
      filterKey,
      expandedNonEmpty: matchingGroupIds.length > 0,
    };
    setExpandedGroupIds(new Set(matchingGroupIds));
  }, [
    groups,
    hasActiveFilters,
    searchQuery,
    statusFilter,
    portfolioFilters.modelPortfolioId,
    portfolioFilters.financialProductId,
  ]);

  useEffect(() => {
    const visibleRowIds = new Set(
      filteredGroups.flatMap((group) => group.rows.map((row) => row.id)),
    );
    setSelectedIds((current) => {
      const next = new Set([...current].filter((id) => visibleRowIds.has(id)));
      return next.size === current.size ? current : next;
    });
  }, [filteredGroups]);

  const toggleGroupExpanded = (portfolioReportId: string) => {
    setExpandedGroupIds((current) => {
      const next = new Set(current);
      if (next.has(portfolioReportId)) {
        next.delete(portfolioReportId);
      } else {
        next.add(portfolioReportId);
      }
      return next;
    });
  };

  const expandAllGroups = () => {
    setExpandedGroupIds(new Set(visibleGroupIds));
  };

  const collapseAllGroups = () => {
    setExpandedGroupIds(new Set());
  };

  const allActionableIds = useMemo(
    () =>
      groups.flatMap((group) =>
        group.rows.filter((row) => row.isActionable).map((row) => row.id),
      ),
    [groups],
  );
  const visibleActionableIds = useMemo(
    () =>
      filteredGroups.flatMap((group) =>
        group.rows.filter((row) => row.isActionable).map((row) => row.id),
      ),
    [filteredGroups],
  );
  const selectionScopeActionableIds = hasActiveFilters
    ? visibleActionableIds
    : allActionableIds;
  const cancelAllActionableIds = selectionScopeActionableIds;
  const selectedActionableIds = useMemo(
    () => selectionScopeActionableIds.filter((id) => selectedIds.has(id)),
    [selectionScopeActionableIds, selectedIds],
  );
  const allActionableSelected =
    selectionScopeActionableIds.length > 0 &&
    selectedActionableIds.length === selectionScopeActionableIds.length;
  const showNoteColumn = useMemo(
    () => hasReviewTradeNotes(groups.flatMap((group) => group.rows)),
    [groups],
  );
  const busy =
    !tradesFullyLoaded || evaluating || cancelling || verifying || proceeding;

  const toggleRow = (id: string) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleAll = () => {
    setSelectedIds((current) => {
      if (allActionableSelected) {
        const next = new Set(current);
        for (const id of selectionScopeActionableIds) {
          next.delete(id);
        }
        return next;
      }

      return new Set([...current, ...selectionScopeActionableIds]);
    });
  };

  const handleCancelSelected = async () => {
    setConfirmCancelSelected(false);
    await onCancelTrades(selectedActionableIds);
    setSelectedIds(new Set());
  };

  const handleCancelAll = async () => {
    setConfirmCancelAll(false);
    await onCancelTrades(cancelAllActionableIds);
    setSelectedIds(new Set());
  };

  const confirmCancelRow = groups
    .flatMap((group) => group.rows)
    .find((row) => row.id === confirmCancelRowId);

  const handleCancelRow = async () => {
    if (!confirmCancelRowId) {
      return;
    }

    const rowId = confirmCancelRowId;
    setConfirmCancelRowId(null);
    await onCancelTrades([rowId]);
    setSelectedIds((current) => {
      const next = new Set(current);
      next.delete(rowId);
      return next;
    });
  };

  const handleApproveSelected = async () => {
    const unverifiedSelected = filteredGroups
      .flatMap((group) => group.rows)
      .filter(
        (row) => selectedIds.has(row.id) && row.isActionable && !row.isVerified,
      )
      .map((row) => row.id);

    await onApproveTrades(unverifiedSelected);
    setSelectedIds(new Set());
  };

  const summaryTags: Array<{
    key: string;
    label: string;
    color: 'positive' | 'warning' | 'supportOne' | 'negative';
  }> = [];

  if (counts.ready > 0) {
    summaryTags.push({
      key: 'ready',
      label: t('reviewTrades.readyCount', { count: counts.ready }),
      color: 'positive',
    });
  }

  if (counts.held > 0) {
    summaryTags.push({
      key: 'held',
      label: t('reviewTrades.heldCount', { count: counts.held }),
      color: 'warning',
    });
  }

  if (counts.pending > 0) {
    summaryTags.push({
      key: 'pending',
      label: t('reviewTrades.pendingCount', { count: counts.pending }),
      color: 'supportOne',
    });
  }

  if (counts.canceled > 0) {
    summaryTags.push({
      key: 'canceled',
      label: t('reviewTrades.canceledCount', { count: counts.canceled }),
      color: 'negative',
    });
  }

  if (requireApproval && counts.unverifiedActionable > 0) {
    summaryTags.push({
      key: 'unverified',
      label: t('reviewTrades.unverifiedCount', {
        count: counts.unverifiedActionable,
      }),
      color: 'warning',
    });
  }

  return (
    <Box
      data-testid='bulk-rebalance-review-trades'
      sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}
    >
      {counts.total > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            pb: 2,
            borderBottom: `1px solid ${theme.color.outlineVariant}`,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              alignItems: 'center',
            }}
          >
            <Typography variant='labelSmall' color='textSecondary'>
              {t('reviewTrades.subtitle', { count: counts.total })}
            </Typography>
            {summaryTags.map((tag) => (
              <Badge key={tag.key} label={tag.label} color={tag.color} small />
            ))}
          </Box>

          {(canApprove || canCancel) && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {canApprove && (
                <Button
                  variant='outlined'
                  size='small'
                  disabled={busy || selectedActionableIds.length === 0}
                  loading={verifying}
                  onClick={() => void handleApproveSelected()}
                  data-testid='bulk-rebalance-review-approve-selected'
                >
                  {t('reviewTrades.approveSelected')}
                </Button>
              )}
              {canCancel && (
                <Button
                  variant='outlined'
                  size='small'
                  disabled={busy || selectedActionableIds.length === 0}
                  loading={cancelling}
                  onClick={() => setConfirmCancelSelected(true)}
                  data-testid='bulk-rebalance-review-cancel-selected'
                >
                  {t('reviewTrades.cancelSelected')}
                </Button>
              )}
              {canCancel && (
                <Button
                  variant='outlined'
                  size='small'
                  color='error'
                  disabled={busy || cancelAllActionableIds.length === 0}
                  onClick={() => setConfirmCancelAll(true)}
                  data-testid='bulk-rebalance-review-cancel-all'
                >
                  {t('reviewTrades.cancelAll')}
                </Button>
              )}
            </Box>
          )}
        </Box>
      )}

      {evaluating && (
        <Alert severity='info' data-testid='bulk-rebalance-review-evaluating'>
          {t('reviewTrades.evaluating')}
        </Alert>
      )}

      {requireApproval && counts.unverifiedActionable > 0 && (
        <Alert
          severity='info'
          data-testid='bulk-rebalance-review-approval-notice'
        >
          {t('reviewTrades.unverifiedNotice')}
        </Alert>
      )}

      {proceedDisabledReason && !showProceedButton && (
        <Alert
          severity='info'
          data-testid='bulk-rebalance-review-proceed-notice'
        >
          {proceedDisabledReason}
        </Alert>
      )}

      {totalCount > counts.total && (
        <Alert severity='info' data-testid='bulk-rebalance-review-truncated'>
          {t('reviewTrades.truncated', {
            shown: counts.total,
            total: totalCount,
          })}
        </Alert>
      )}

      {loading ? (
        <Box data-testid='bulk-rebalance-review-loading'>
          <Skeleton variant='rectangular' height={160} />
        </Box>
      ) : counts.total === 0 ? (
        <Typography
          variant='bodyMedium'
          color='textSecondary'
          data-testid='bulk-rebalance-review-empty'
        >
          {t('reviewTrades.empty')}
        </Typography>
      ) : (
        <>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <BulkRebalancePortfolioFilterBar
              searchPlaceholder={t('reviewTrades.filterSearchPlaceholder')}
              searchTestId='bulk-rebalance-review-filter-search'
              modelTestId='bulk-rebalance-review-filter-model'
              assetTestId='bulk-rebalance-review-filter-asset'
              clearTestId='bulk-rebalance-review-filter-clear'
              value={portfolioFilters}
              modelOptions={filterIndex.modelOptions}
              assetOptions={filterIndex.assetOptions}
              onChange={setPortfolioFilters}
              leading={
                <Box
                  sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}
                  data-testid='bulk-rebalance-review-status-filters'
                >
                  {visibleStatusFilters.map((filter) => {
                    const isSelected = statusFilter === filter;

                    return (
                      <Chip
                        key={filter}
                        text={t(`reviewTrades.filterStatus.${filter}`)}
                        small
                        onClick={() =>
                          setStatusFilter(isSelected ? 'all' : filter)
                        }
                        sx={
                          isSelected
                            ? {
                                backgroundColor: theme.color.primaryVariant,
                                borderColor: theme.color.primary,
                                '&:hover': {
                                  backgroundColor:
                                    theme.color.primaryVariantHover,
                                  borderColor: theme.color.primary,
                                },
                              }
                            : undefined
                        }
                      />
                    );
                  })}
                </Box>
              }
              trailing={
                <>
                  <Button
                    variant='text'
                    size='small'
                    disabled={allVisibleExpanded}
                    onClick={expandAllGroups}
                    data-testid='bulk-rebalance-review-expand-all'
                  >
                    {t('reviewTrades.expandAll')}
                  </Button>
                  <Button
                    variant='text'
                    size='small'
                    disabled={expandedGroupIds.size === 0}
                    onClick={collapseAllGroups}
                    data-testid='bulk-rebalance-review-collapse-all'
                  >
                    {t('reviewTrades.collapseAll')}
                  </Button>
                </>
              }
            />

            {hasActiveFilters && filteredGroups.length > 0 && (
              <Typography
                variant='labelSmall'
                color='textSecondary'
                data-testid='bulk-rebalance-review-filter-results'
              >
                {t('reviewTrades.filterResults', {
                  portfolioCount: filteredGroups.length,
                  tradeCount: filteredTradeCount,
                })}
              </Typography>
            )}
          </Box>

          {filteredGroups.length === 0 ? (
            <Typography
              variant='bodyMedium'
              color='textSecondary'
              data-testid='bulk-rebalance-review-filter-empty'
            >
              {t('reviewTrades.filterNoMatches')}
            </Typography>
          ) : (
            <Box
              sx={{
                overflowX: 'auto',
                border: `1px solid ${theme.color.outlineVariant}`,
                borderRadius: theme.borderRadius.md,
              }}
            >
              <Table
                sx={{
                  ...tableHeadDividerSx,
                  '& thead .MuiTableCell-root': {
                    ...tableHeadDividerSx['& thead .MuiTableCell-root'],
                    paddingTop: 1.25,
                    paddingBottom: 1.25,
                  },
                  '& tbody .MuiTableCell-root': {
                    verticalAlign: 'middle',
                  },
                }}
                data-testid='bulk-rebalance-review-table'
              >
                <TableHead>
                  <TableRow>
                    <TableHeadCell sx={{ width: 48 }} headerBare>
                      {canCancel || canApprove ? (
                        <Checkbox
                          checked={allActionableSelected}
                          indeterminate={
                            selectedActionableIds.length > 0 &&
                            !allActionableSelected
                          }
                          disabled={
                            busy || selectionScopeActionableIds.length === 0
                          }
                          onChange={() => toggleAll()}
                          slotProps={{
                            input: {
                              'data-testid': 'bulk-rebalance-review-select-all',
                              'aria-label': t('reviewTrades.selectAllAria'),
                            },
                          }}
                        />
                      ) : null}
                    </TableHeadCell>
                    {showTradeColumns ? (
                      <>
                        <TableHeadCell sx={{ minWidth: 220 }}>
                          {t('reviewTrades.columns.security')}
                        </TableHeadCell>
                        <TableHeadCell sx={{ width: 72 }}>
                          {t('reviewTrades.columns.type')}
                        </TableHeadCell>
                        <TableHeadCell number right sx={{ width: 128 }}>
                          {t('reviewTrades.columns.amount')}
                        </TableHeadCell>
                        <TableHeadCell number right sx={{ width: 96 }}>
                          {t('reviewTrades.columns.price')}
                        </TableHeadCell>
                        <TableHeadCell number right sx={{ width: 88 }}>
                          {t('reviewTrades.columns.shares')}
                        </TableHeadCell>
                        <TableHeadCell sx={{ width: 120 }}>
                          {t('reviewTrades.columns.account')}
                        </TableHeadCell>
                        <TableHeadCell sx={{ width: 112 }}>
                          {t('reviewTrades.columns.status')}
                        </TableHeadCell>
                        {showNoteColumn && (
                          <TableHeadCell sx={{ minWidth: 160 }}>
                            {t('reviewTrades.columns.note')}
                          </TableHeadCell>
                        )}
                        <TableHeadCell sx={{ width: 88 }} />
                      </>
                    ) : (
                      <>
                        <TableHeadCell sx={{ minWidth: 240 }}>
                          {t('reviewTrades.columns.portfolio')}
                        </TableHeadCell>
                        <TableHeadCell sx={{ minWidth: 140 }}>
                          {t('reviewTrades.columns.sleeves')}
                        </TableHeadCell>
                        <TableHeadCell number right sx={{ width: 200 }}>
                          {t('reviewTrades.columns.amount')}
                        </TableHeadCell>
                        <TableHeadCell sx={{ width: 140 }}>
                          {t('reviewTrades.columns.status')}
                        </TableHeadCell>
                      </>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredGroups.map((group) => (
                    <ReviewTradeGroupRows
                      key={group.portfolioReportId}
                      group={group}
                      expanded={expandedGroupIds.has(group.portfolioReportId)}
                      showTradeColumns={showTradeColumns}
                      onToggleExpanded={() =>
                        toggleGroupExpanded(group.portfolioReportId)
                      }
                      selectedIds={selectedIds}
                      busy={busy}
                      canCancel={canCancel}
                      canSelect={canCancel || canApprove}
                      onToggle={toggleRow}
                      onCancelRow={setConfirmCancelRowId}
                      localizedDate={localizedDate}
                      showNoteColumn={showNoteColumn}
                      statusFilter={statusFilter}
                    />
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </>
      )}

      <ReviewTradeCancelDialogs
        confirmCancelSelected={confirmCancelSelected}
        confirmCancelAll={confirmCancelAll}
        rowDialogOpen={!!confirmCancelRowId}
        confirmCancelRow={confirmCancelRow}
        selectedCount={selectedActionableIds.length}
        cancelAllCount={cancelAllActionableIds.length}
        cancelAllFiltered={hasActiveFilters}
        onCloseSelected={() => setConfirmCancelSelected(false)}
        onCloseAll={() => setConfirmCancelAll(false)}
        onCloseRow={() => setConfirmCancelRowId(null)}
        onConfirmSelected={() => void handleCancelSelected()}
        onConfirmAll={() => void handleCancelAll()}
        onConfirmRow={() => void handleCancelRow()}
      />
    </Box>
  );
};
